import { Box, Group, Stack, Tooltip, useMantineTheme } from '@mantine/core';
import { IconCopyOff, IconDatabase, IconExclamationCircle, IconEyeOff, IconPencilOff } from '@tabler/icons-react';
import Toast from 'components/Toast';
import P3Medium from 'components/typography/P3Medium';
import P3Regular from 'components/typography/P3Regular';
import { noop } from 'lodash';
import { INPUT_TYPE_NAMES } from 'pages/revisions-module/template-editor/editors/form/configurator/InputTypeSelect';
import CertificateInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/CertificateInputPreview';
import CheckboxInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/CheckboxInputPreview';
import ConstantInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/ConstantInputPreview';
import ComparatorInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/ComparatorInputPreview';
import DateInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/DateInputPreview';
import DatePlusPeriodInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/DatePlusPeriodInputPreview';
import DeviceEvidenceFieldInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/DeviceEvidenceFieldInputPreview';
import DevicePropertyInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/DevicePropertyInputPreview';
import FaultSeverityInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/FaultSeverityInputPreview';
import FaultTypeInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/FaultTypeInputPreview';
import FieldUpdatedAtInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/FieldUpdatedAtInputPreview';
import FileListInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/FileListInputPreview';
import IfElseInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/IfElseInputPreview';
import MeasuringDeviceInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/MeasuringDeviceInputPreview';
import DeviceFieldAggregatorInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/DeviceFieldAggregatorInputPreview';
import MeasurementColumnAggregatorInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/MeasurementColumnAggregatorInputPreview';
import TextExpressionInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/TextExpressionInputPreview';
import NumberInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/NumberInputPreview';
import NumberOfMeasurementsInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/NumberOfMeasurementsInputPreview';
import PeriodInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/PeriodInputPreview';
import PredefinedListInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/PredefinedListInputPreview';
import PredefinedSelectInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/PredefinedSelectInputPreview';
import QualificationInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/QualificationInputPreview';
import SafetyPointInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/SafetyPointInputPreview';
import SelectInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/SelectInputPreview';
import SumInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/SumInputPreview';
import TextInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/TextInputPreview';
import WysiwygInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/WysiwygInputPreview';
import ValueMapInputPreview from 'pages/revisions-module/template-editor/editors/form/input/previews/ValueMapInputPreview';
import { IFormInput } from 'pages/revisions-module/template-editor/editors/form/types';
import { useMemo } from 'react';

type TypeCond<T, U> = [T] extends [never] ? { message: U } : {};

interface FormInputPreviewProps {
  data: IFormInput;
  draggable: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
}

/**
 * The discriminator of the form input preview.
 */
function FormInputPreviewDiscriminator({ data }: { data: IFormInput }) {
  switch (data.spec.type) {
    case 'text':
      return <TextInputPreview spec={data.spec} />;

    case 'number':
      return <NumberInputPreview spec={data.spec} />;

    case 'date':
      return <DateInputPreview spec={data.spec} />;

    case 'checkbox':
      return <CheckboxInputPreview spec={data.spec} />;

    case 'certificate':
      return <CertificateInputPreview spec={data.spec} />;

    case 'qualification':
      return <QualificationInputPreview spec={data.spec} />;

    case 'select':
      return <SelectInputPreview spec={data.spec} />;

    case 'wysiwyg':
      return <WysiwygInputPreview spec={data.spec} />;

    case 'constant':
      return <ConstantInputPreview spec={data.spec} />;

    case 'valueMap':
      return <ValueMapInputPreview spec={data.spec} />;

    case 'ifElse':
      return <IfElseInputPreview spec={data.spec} />;

    case 'comparator':
      return <ComparatorInputPreview spec={data.spec} />;

    case 'predefinedSelect':
      return <PredefinedSelectInputPreview spec={data.spec} />;

    case 'predefinedList':
      return <PredefinedListInputPreview spec={data.spec} />;

    case 'measuringDevice':
      return <MeasuringDeviceInputPreview spec={data.spec} />;

    case 'period':
      return <PeriodInputPreview spec={data.spec} />;

    case 'safetyPoint':
      return <SafetyPointInputPreview spec={data.spec} />;

    case 'deviceProperty':
      return <DevicePropertyInputPreview spec={data.spec} />;

    case 'deviceEvidenceField':
      return <DeviceEvidenceFieldInputPreview spec={data.spec} />;

    case 'sum':
      return <SumInputPreview spec={data.spec} />;

    case 'datePlusPeriod':
      return <DatePlusPeriodInputPreview spec={data.spec} />;

    case 'faultType':
      return <FaultTypeInputPreview spec={data.spec} />;

    case 'faultSeverity':
      return <FaultSeverityInputPreview spec={data.spec} />;

    case 'numberOfMeasurements':
      return <NumberOfMeasurementsInputPreview spec={data.spec} />;

    case 'measurementColumnAggregator':
      return <MeasurementColumnAggregatorInputPreview spec={data.spec} />;

    case 'deviceFieldAggregator':
      return <DeviceFieldAggregatorInputPreview spec={data.spec} />;

    case 'textExpression':
      return <TextExpressionInputPreview spec={data.spec} />;

    case 'fieldUpdatedAt':
      return <FieldUpdatedAtInputPreview spec={data.spec} />;

    case 'fileList':
      return <FileListInputPreview spec={data.spec} />;

    default:
      // This is a TS trick to make sure all types are handled in the switch.
      // If there is a new type, the compiler will complain here. To fix it, add a new case to the switch.
      const { message }: TypeCond<typeof data.spec, string> = { message: 'Neznámy typ poľa' };

      return (
        <Box mr={32}>
          <Toast fullWidth type="fail" withCloseButton={false} message={message} />
        </Box>
      );
  }
}

/**
 * The width label of a form input preview.
 */
function WidthLabel({ data }: { data: IFormInput }) {
  if (data.spec.minWidth && data.spec.maxWidth && data.spec.minWidth === data.spec.maxWidth) {
    return <P3Regular color="gray.6">({data.spec.minWidth} px)</P3Regular>;
  }

  if (data.spec.minWidth && data.spec.maxWidth) {
    return (
      <P3Regular color="gray.6">
        ({data.spec.minWidth} - {data.spec.maxWidth} px)
      </P3Regular>
    );
  }

  if (data.spec.minWidth) {
    return <P3Regular color="gray.6">(&ge; {data.spec.minWidth} px)</P3Regular>;
  }

  if (data.spec.maxWidth) {
    return <P3Regular color="gray.6">(&le; {data.spec.maxWidth} px)</P3Regular>;
  }

  return null;
}

/**
 * The label of a form input preview.
 */
export function FormInputPreviewLabel({
  data,
  draggable,
  onDragStart = noop,
  onDragEnd = noop,
}: FormInputPreviewProps) {
  const theme = useMantineTheme();

  const typeName = useMemo(() => INPUT_TYPE_NAMES[data.spec.type].label, [data.spec.type]);

  return (
    <Group spacing={4} pr={32}>
      {!data.deletable && (
        <Tooltip label="Pole sa musí nachádzať vo formulári" withArrow={false} openDelay={200}>
          <IconExclamationCircle size={16} color={theme.colors.gray[6]} />
        </Tooltip>
      )}
      {!data.spec.copyable && (
        <Tooltip label="Údaj sa nekopíruje" withArrow={false} openDelay={200}>
          <IconCopyOff size={16} color={theme.colors.gray[6]} />
        </Tooltip>
      )}
      {data.spec.readOnly && (
        <Tooltip label="Údaj iba na čítanie" withArrow={false} openDelay={200}>
          <IconPencilOff size={16} color={theme.colors.gray[6]} />
        </Tooltip>
      )}
      {data.spec.hidden && (
        <Tooltip label="Skryté pole" withArrow={false} openDelay={200}>
          <IconEyeOff size={16} color={theme.colors.gray[6]} />
        </Tooltip>
      )}
      {data.spec.index && (
        <Tooltip label="Indexované pole" withArrow={false} openDelay={200}>
          <IconDatabase size={16} color={theme.colors.gray[6]} />
        </Tooltip>
      )}
      <Group
        spacing={4}
        draggable={draggable}
        className={draggable ? 'cursor-grab' : ''}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <P3Medium color="gray.8">{data.spec.label}</P3Medium>
        {data.spec.required && <P3Regular color="red.8">*</P3Regular>}
        <P3Regular color="gray.6">&mdash; {typeName}</P3Regular>
        <WidthLabel data={data} />
      </Group>
    </Group>
  );
}

/**
 * The preview of a form input.
 */
export default function FormInputPreview({ data, draggable, onDragStart, onDragEnd }: FormInputPreviewProps) {
  return (
    <Stack spacing={4} w="100%">
      <FormInputPreviewLabel
        data={data}
        draggable={draggable}
        onDragStart={draggable ? onDragStart : noop}
        onDragEnd={draggable ? onDragEnd : noop}
      />
      <FormInputPreviewDiscriminator data={data} />
    </Stack>
  );
}
